import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Plus, Trash2, Loader2, ImagePlus, X, Smartphone, Save, Pencil, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const CONDITIONS = ["Əla", "Yaxşı", "Normal", "Zədəli"];
const empty = {
  name: "", image_url: "", images: [], storage: "", color: "", condition: "Əla", price: "", order: "",
  description: "", battery_replaced: false, battery_health: "", faceid_working: true, scuffs: false, chips: false, in_stock: true,
  registered: true, beyanname: true, wifi_working: true, device_type: "phone", connectivity: "", case_size: "", watch_material: "", sim_type: "dual",
};

const Toggle = ({ label, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`shrink-0 whitespace-nowrap px-3 py-2 rounded-lg text-xs border transition-colors ${active ? "border-white/70 bg-white/[0.06] text-white" : "border-white/10 text-neutral-400"}`}>
    {label}
  </button>
);

export const ListingsManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/listings").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const slots = Math.max(0, 6 - form.images.length);
      for (const file of files.slice(0, slots)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url], image_url: f.image_url || data.url }));
      }
    } catch { toast.error("Şəkil yüklənmədi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (i) => setForm((f) => {
    const images = f.images.filter((_, x) => x !== i);
    return { ...f, images, image_url: images[0] || "" };
  });

  const save = async () => {
    if (!form.name.trim() || !form.price) { toast.error("Ad və qiymət tələb olunur"); return; }
    setSaving(true);
    const payload = { ...form, price: parseInt(form.price) || 0, order: parseInt(form.order) || 0 };
    try {
      if (editingId) await api.put(`/admin/listings/${editingId}`, payload);
      else await api.post("/admin/listings", payload);
      setForm(empty); setEditingId(null);
      await load();
      toast.success(editingId ? "Yeniləndi" : "Əlavə edildi");
    } catch { toast.error("Xəta baş verdi"); }
    finally { setSaving(false); }
  };

  const edit = (l) => { setForm({ ...empty, ...l, images: (l.images && l.images.length) ? l.images : (l.image_url ? [l.image_url] : []), price: String(l.price ?? ""), order: String(l.order ?? ""), battery_health: l.battery_health ? String(l.battery_health) : "" }); setEditingId(l.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setForm(empty); setEditingId(null); };
  const remove = async (id) => { await api.delete(`/admin/listings/${id}`); load(); toast.success("Silindi"); };

  return (
    <div className="space-y-6" data-testid="listings-manager">
      {/* form */}
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium">
          {editingId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editingId ? "Telefonu redaktə et" : "Satışa telefon əlavə et"}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ad (iPhone 13 Pro)" data-testid="listing-name" className="bg-white/5 border-white/10" />
          <select value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })} data-testid="listing-device-type"
            className="bg-white/5 border border-white/10 rounded-md h-10 px-3 text-sm text-neutral-200">
            <option value="phone" className="bg-[#111]">iPhone</option>
            <option value="tablet" className="bg-[#111]">iPad</option>
            <option value="watch" className="bg-[#111]">Apple Watch</option>
          </select>
          <Input value={form.storage} onChange={(e) => setForm({ ...form, storage: e.target.value })} placeholder="Yaddaş (256GB)" data-testid="listing-storage" className="bg-white/5 border-white/10" />
          <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Rəng (Qrafit)" data-testid="listing-color" className="bg-white/5 border-white/10" />
          {form.device_type === "phone" && (
            <select value={form.sim_type} onChange={(e) => setForm({ ...form, sim_type: e.target.value })} data-testid="listing-sim-type"
              className="bg-white/5 border border-white/10 rounded-md h-10 px-3 text-sm text-neutral-200">
              <option value="dual" className="bg-[#111]">Fiziki SIM + eSIM</option>
              <option value="dualsim" className="bg-[#111]">Dual SIM + eSIM</option>
              <option value="esim" className="bg-[#111]">Yalnız eSIM</option>
            </select>
          )}
          <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Satış qiyməti (₼)" type="number" data-testid="listing-price" className="bg-white/5 border-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500 shrink-0">Vəziyyət:</span>
            <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="listing-condition"
              className="flex-1 bg-white/5 border border-white/10 rounded-md h-10 px-3 text-sm text-neutral-200">
              {CONDITIONS.map((c) => <option key={c} value={c} className="bg-[#111]">{c}</option>)}
            </select>
          </div>
          <Input value={form.battery_health} onChange={(e) => setForm({ ...form, battery_health: e.target.value })} placeholder="Batareya %" type="number" data-testid="listing-battery-health" className="bg-white/5 border-white/10" />
          <Input value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Sıra" type="number" data-testid="listing-order" className="bg-white/5 border-white/10" />
        </div>

        {/* photos — up to 6 */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-neutral-500">Şəkillər ({form.images.length}/6)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.images.map((img, i) => (
              <div key={i} className="relative">
                <img src={fileUrl(img)} alt="" className="w-16 h-16 rounded-lg object-contain bg-white/5 border border-white/10" />
                <button onClick={() => removeImage(i)} data-testid={`listing-image-remove-${i}`} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black border border-white/20 flex items-center justify-center"><X className="w-3 h-3" /></button>
                {i === 0 && <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center bg-black/70 text-neutral-300 rounded-b-lg">Əsas</span>}
              </div>
            ))}
            {form.images.length < 6 && (
              <>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
                <button onClick={() => fileRef.current?.click()} data-testid="listing-image-upload"
                  className="w-16 h-16 rounded-lg border border-dashed border-white/15 text-neutral-400 hover:text-white hover:border-white/30 transition-colors flex flex-col items-center justify-center gap-1">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  <span className="text-[9px]">Əlavə et</span>
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-nowrap gap-2 mt-3 overflow-x-auto pb-1">
          <Toggle label={form.battery_replaced ? "Batareya: dəyişilib" : "Batareya: orijinal"} active={form.battery_replaced} onClick={() => setForm({ ...form, battery_replaced: !form.battery_replaced })} testid="listing-battery" />
          <Toggle label={form.faceid_working ? "Face ID: işlək" : "Face ID: yox"} active={!form.faceid_working} onClick={() => setForm({ ...form, faceid_working: !form.faceid_working })} testid="listing-faceid" />
          <Toggle label={form.scuffs ? "Cızıq: var" : "Cızıq: yox"} active={form.scuffs} onClick={() => setForm({ ...form, scuffs: !form.scuffs })} testid="listing-scuffs" />
          <Toggle label={form.chips ? "Əzik: var" : "Əzik: yox"} active={form.chips} onClick={() => setForm({ ...form, chips: !form.chips })} testid="listing-chips" />
          <Toggle label={form.registered ? "Qeydiyyat: var" : "Qeydiyyat: yox"} active={form.registered} onClick={() => setForm({ ...form, registered: !form.registered })} testid="listing-registered" />
          <Toggle label={form.beyanname ? "Bəyannamə: var" : "Bəyannamə: yox"} active={form.beyanname} onClick={() => setForm({ ...form, beyanname: !form.beyanname })} testid="listing-beyanname" />
          <Toggle label={form.wifi_working ? "Wi‑Fi: işlək" : "Wi‑Fi: yox"} active={!form.wifi_working} onClick={() => setForm({ ...form, wifi_working: !form.wifi_working })} testid="listing-wifi" />
          <Toggle label={form.in_stock ? "Stokda" : "Satıldı"} active={form.in_stock} onClick={() => setForm({ ...form, in_stock: !form.in_stock })} testid="listing-instock" />
        </div>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Əlavə təsvir (istəyə görə)" data-testid="listing-desc" className="bg-white/5 border-white/10 mt-3" />
        <div className="flex gap-2 mt-3">
          <button onClick={save} disabled={saving} data-testid="listing-save-btn"
            className="px-5 py-2.5 rounded-xl bg-neutral-100 text-black text-sm font-medium disabled:opacity-50 hover:bg-white transition-colors flex items-center gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingId ? "Yadda saxla" : "Əlavə et"}
          </button>
          {editingId && <button onClick={cancel} className="px-5 py-2.5 rounded-xl border border-white/15 text-sm text-neutral-300 hover:bg-white/5 transition-colors">Ləğv et</button>}
        </div>
      </div>

      {/* list */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((l) => (
          <div key={l.id} className="glass rounded-2xl overflow-hidden" data-testid={`listing-row-${l.id}`}>
            <div className="h-40 bg-[#0d0d0d] flex items-center justify-center p-3">
              {l.image_url ? <img src={fileUrl(l.image_url)} alt={l.name} className="h-full object-contain" /> : <Smartphone className="w-8 h-8 text-neutral-600" />}
            </div>
            <div className="p-4">
              <div className="flex items-baseline justify-between">
                <div className="font-medium text-sm">{l.name}</div>
                <div className="font-display font-medium">{(l.price || 0).toLocaleString("ru-RU")} ₼</div>
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">{l.storage}{l.color ? ` · ${l.color}` : ""} · {l.condition}{l.in_stock ? "" : " · Satıldı"}</div>
              <div className="flex items-center gap-1.5 text-[11px] text-[#2997FF] mt-1.5" data-testid={`listing-views-${l.id}`} title="Baxış sayı">
                <Eye className="w-3.5 h-3.5" /> {(l.views || 0).toLocaleString("ru-RU")} baxış
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => edit(l)} data-testid={`listing-edit-${l.id}`}
                  className="flex-1 h-9 rounded-lg border border-white/10 text-sm text-neutral-300 hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5"><Pencil className="w-4 h-4" /> Redaktə</button>
                <button onClick={() => remove(l.id)} data-testid={`listing-delete-${l.id}`}
                  className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
